import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { api } from '../services/api';
import {
  MAX_DEVICE_DISPLAY_NAME_LEN,
  buildDeviceNickname,
  getDeviceDisplayName,
  getDeviceMacForNickname,
  normalizeMacForNickname,
} from '../utils/deviceDisplayUtils';
import { filterLastKnownGoodEligible, LKG_INELIGIBLE_MSG } from '../utils/lkgUtils';

const DeviceNicknameSelectionPanel = ({
  devices = [],
  onNotify,
  onRefreshDevice,
  saving = false,
}) => {
  const eligibleDevices = filterLastKnownGoodEligible(devices);
  const ineligibleCount = devices.length - eligibleDevices.length;
  const [namePrefix, setNamePrefix] = useState('');
  const [actionLoading, setActionLoading] = useState('');

  const notify = (payload) => {
    if (typeof onNotify === 'function') {
      onNotify(payload);
    }
  };

  const refreshDevice = async (serial) => {
    if (typeof onRefreshDevice === 'function') {
      await onRefreshDevice(serial);
    }
  };

  const previews = useMemo(() => eligibleDevices.map((device) => {
    const serial = device.serial_number?.toLowerCase();
    const macRaw = getDeviceMacForNickname(device);
    const built = buildDeviceNickname(namePrefix, device);
    return {
      device,
      serial,
      macRaw,
      mac: normalizeMacForNickname(macRaw),
      built,
    };
  }), [eligibleDevices, namePrefix]);

  const applyable = previews.filter((row) => row.built?.nickname);
  const missingMac = previews.filter((row) => !row.mac);
  const tooLong = previews.filter((row) => row.built?.tooLong);

  const handleApplyOne = async (row) => {
    const nickname = row.built?.nickname;
    if (!row.serial || !nickname || saving || actionLoading) return;

    setActionLoading(`apply-${row.serial}`);
    try {
      await api.patchDeviceMetadata(row.serial, { displayName: nickname });
      await refreshDevice(row.serial);
      notify({
        type: 'success',
        message: `Nickname set to "${nickname}" for ${getDeviceDisplayName(row.device)}`,
      });
    } catch (err) {
      notify({ type: 'error', message: err.message || `Failed to set nickname for ${row.serial}` });
    } finally {
      setActionLoading('');
    }
  };

  const handleApplyAll = async () => {
    const trimmed = namePrefix.trim();
    if (!trimmed) {
      notify({ type: 'warning', message: 'Enter a name prefix first' });
      return;
    }
    if (applyable.length === 0) {
      notify({ type: 'warning', message: 'No selected devices have a Jnet2 device MAC for nickname generation' });
      return;
    }

    setActionLoading('apply-all');
    let ok = 0;
    try {
      for (const row of applyable) {
        try {
          await api.patchDeviceMetadata(row.serial, { displayName: row.built.nickname });
          await refreshDevice(row.serial);
          ok += 1;
        } catch {
          // continue with remaining devices
        }
      }
      notify({
        type: ok === applyable.length ? 'success' : (ok > 0 ? 'warning' : 'error'),
        message: `Set nicknames for ${ok}/${applyable.length} device(s)`,
      });
    } finally {
      setActionLoading('');
    }
  };

  if (devices.length === 0) {
    return <Alert severity="info">No devices selected.</Alert>;
  }

  return (
    <Box>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Nick Name
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set display names for selected jnet2 devices using your prefix plus each Jnet2 device MAC:
            {' '}
            <strong>{'{prefix}_{mac}'}</strong>
          </Typography>

          {ineligibleCount > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {ineligibleCount} selected device(s) are not jnet2 CAP/Node and are excluded. {LKG_INELIGIBLE_MSG}
            </Alert>
          )}

          {eligibleDevices.length === 0 ? (
            <Alert severity="warning">{LKG_INELIGIBLE_MSG}</Alert>
          ) : (
            <>
              <TextField
                fullWidth
                size="small"
                label="Name prefix"
                placeholder="e.g. Lamp"
                value={namePrefix}
                onChange={(e) => setNamePrefix(e.target.value)}
                helperText={`Pattern: prefix_<jnet2_device_mac> (max ${MAX_DEVICE_DISPLAY_NAME_LEN} characters)`}
                inputProps={{ maxLength: MAX_DEVICE_DISPLAY_NAME_LEN }}
                sx={{ mb: 2 }}
              />

              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Chip label={`${eligibleDevices.length} eligible`} size="small" color="primary" />
                <Chip label={`${applyable.length} ready`} size="small" variant="outlined" />
                {missingMac.length > 0 && (
                  <Chip label={`${missingMac.length} missing Jnet2 MAC`} size="small" color="warning" variant="outlined" />
                )}
              </Stack>

              {tooLong.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Prefix is too long for {tooLong.length} device(s) once the Jnet2 device MAC suffix is appended.
                </Alert>
              )}

              <Button
                variant="contained"
                disabled={!!actionLoading || saving || !namePrefix.trim() || applyable.length === 0}
                startIcon={actionLoading === 'apply-all' ? <CircularProgress size={16} color="inherit" /> : null}
                onClick={handleApplyAll}
                sx={{ mb: 3 }}
              >
                Apply nicknames to all
              </Button>

              <Stack spacing={1.5}>
                {previews.map((row) => {
                  const busy = actionLoading === `apply-${row.serial}`;
                  const currentName = getDeviceDisplayName(row.device);
                  const proposed = row.built?.nickname;

                  return (
                    <Card key={row.serial} variant="outlined" sx={{ backgroundColor: '#fafafa' }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                          <Box>
                            <Typography variant="subtitle2">{row.serial}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Current: {currentName}
                            </Typography>
                            <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.75 }}>
                              {row.macRaw ? (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`MAC: ${row.macRaw}`}
                                  sx={{ height: 22, fontSize: '0.7rem' }}
                                />
                              ) : (
                                <Chip
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  label="No Jnet2 MAC available"
                                  sx={{ height: 22, fontSize: '0.7rem' }}
                                />
                              )}
                              {proposed && (
                                <Chip
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  label={`→ ${proposed}`}
                                  sx={{ height: 22, fontSize: '0.7rem' }}
                                />
                              )}
                              {row.built?.tooLong && (
                                <Chip
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  label="Too long"
                                  sx={{ height: 22, fontSize: '0.7rem' }}
                                />
                              )}
                            </Stack>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={busy || !!actionLoading || saving || !proposed}
                            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
                            onClick={() => handleApplyOne(row)}
                          >
                            Apply
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DeviceNicknameSelectionPanel;
